import * as React from 'react';
import throttle from 'lodash/throttle';

import './FadeVirtuosoScroller.modules.scss';

const SCROLL_TOLERANCE = 1;
const THROTTLE_DELAY = 100;

type Props = React.ComponentPropsWithRef<'div'> & {
  hideScrollbar?: boolean;
};

const FadeVirtuosoScroller = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const {
      style,
      onScroll,
      className,
      hideScrollbar = false,
      ...rest
    } = props;
    const [showFade, setShowFade] = React.useState(false);
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    const updateFade = React.useMemo(
      () =>
        throttle((el: HTMLDivElement) => {
          const hasScroll = el.scrollHeight > el.clientHeight;
          const atBottom =
            el.scrollTop + el.clientHeight >=
            el.scrollHeight - SCROLL_TOLERANCE;
          setShowFade(hasScroll && !atBottom);
        }, THROTTLE_DELAY),
      []
    );

    const handleScroll: React.UIEventHandler<HTMLDivElement> =
      React.useCallback(
        (e) => {
          if (onScroll) {
            (onScroll as React.UIEventHandler<HTMLDivElement>)(e);
          }
          updateFade(e.currentTarget);
        },
        [onScroll, updateFade]
      );

    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }

        if (node) {
          updateFade(node);
        }
      },
      [ref, updateFade]
    );

    return (
      <div styleName="fade-virtuoso-scroller__wrapper">
        <div
          {...rest}
          ref={setRefs}
          style={style}
          className={className}
          styleName={
            hideScrollbar ? 'fade-virtuoso-scroller_hide-scrollbar' : undefined
          }
          onScroll={handleScroll}
        />
        {showFade && <div styleName="fade-virtuoso-scroller__fade" />}
      </div>
    );
  }
);

export default FadeVirtuosoScroller;
